import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const useFavorites = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["favorites"], queryFn: async () => (await api.get("/favorites")).data, enabled: !!user, staleTime: 30000 });
  const ids = new Set(data?.design_ids || []);
  const toggle = async (designId) => {
    const on = ids.has(designId);
    try {
      await (on ? api.delete(`/favorites/${designId}`) : api.post(`/favorites/${designId}`));
      qc.invalidateQueries({ queryKey: ["favorites"] });
      toast.success(on ? "Removed from favorites" : "Saved to favorites");
    } catch { toast.error("Could not update favorites"); }
  };
  return { ids, designs: data?.designs || [], toggle, isLoggedIn: !!user };
};

export const FavoriteButton = ({ designId, className = "", size = 16 }) => {
  const { ids, toggle, isLoggedIn } = useFavorites();
  const navigate = useNavigate();
  const on = ids.has(designId);
  const onClick = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!isLoggedIn) { toast("Sign in to save favorites", { action: { label: "Sign in", onClick: () => navigate("/login") } }); return; }
    toggle(designId);
  };
  return (
    <button type="button" onClick={onClick} aria-label={on ? "Remove from favorites" : "Save to favorites"} data-testid={`favorite-btn-${designId}`}
      className={`inline-flex items-center justify-center rounded-full bg-[#0c0d10]/80 backdrop-blur border border-white/15 shadow-md p-2 transition-transform hover:scale-110 active:scale-95 ${on ? "text-[#ff4d6d]" : "text-white hover:text-[#ff4d6d]"} ${className}`}>
      <Heart size={size} fill={on ? "currentColor" : "none"} />
    </button>
  );
};
